import {
  useChangeActionItem,
  useInvalidateHome,
} from "@coaching-culture/api-client";
import { HomeTodoCategory, HomeTodoDto } from "@coaching-culture/types";
import {
  Button,
  EmptyState,
  LinkButton,
  Skeleton,
  TabList,
  Text,
  useToast,
  typeSize,
} from "@coaching-culture/ui";
import { getUnixTime } from "date-fns";
import { lighten } from "polished";
import React, { useMemo, useState } from "react";
import { FaCheck, FaRegCheckCircle } from "react-icons/fa";
import { Link } from "react-router-dom";
import { useLocalStorage } from "react-use";
import styled, { css } from "styled-components";
import { CategoryLabel, HomeCard, LoadError, Muted } from "./styles";
import { trackHome } from "./track";
import {
  categoryLabels,
  completedText,
  conversationUrl,
  dueText,
  groupLabels,
  groupOrder,
  groupTodos,
  isCompleted,
  isStaleCompletion,
  primaryAction,
  tagLabel,
  TodoGroupId,
} from "./todoPresentation";

/** Rows shown per group before "Show N more". */
const GROUP_PREVIEW = 5;

type Tab = "all" | HomeTodoCategory;

const tabOrder: Tab[] = [
  "all",
  "conversations",
  "feedback",
  "goals",
  "learning",
];

// The shared tab list, tightened to sit under a card header.
const FilterTabs = styled(TabList)`
  margin-bottom: 0;
  padding: 0 8px;

  button {
    font-size: ${typeSize("small")};
    padding: 12px 12px 10px;
    min-height: 44px;
    white-space: nowrap;
  }
`;

const Count = styled.span`
  margin-left: 6px;
  font-size: ${typeSize("caption")};
  font-weight: 700;
`;

const GroupHeading = styled.h3<{ group: TodoGroupId }>`
  margin: 0;
  padding: 6px 18px;
  font-size: ${typeSize("caption")};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: ${(p) =>
    p.group === "overdue"
      ? lighten(0.5, p.theme.colors.danger)
      : p.theme.colors.grey6};
  color: ${(p) =>
    p.group === "overdue" ? p.theme.colors.danger : p.theme.colors.body};
`;

const RowList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 18px;
  border-top: 1px solid ${(p) => p.theme.colors.grey4};

  &:first-child {
    border-top: 0;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 8px 12px;
  }
`;

const RowTag = styled.div`
  width: 96px;
  flex: none;

  @media (max-width: 600px) {
    width: auto;
  }
`;

const RowText = styled.div`
  flex: 1;
  min-width: 0;

  @media (max-width: 600px) {
    flex-basis: 100%;
    order: 2;
  }
`;

const RowTitle = styled(Link)<{ $done?: boolean }>`
  display: block;
  font-weight: 600;
  color: ${(p) => p.theme.colors.black};
  overflow-wrap: anywhere;

  &:hover {
    color: ${(p) => p.theme.colors.black};
  }

  ${(p) =>
    p.$done &&
    css`
      text-decoration: line-through;
      color: ${p.theme.colors.body};

      &:hover {
        color: ${p.theme.colors.body};
      }
    `}
`;

const RowAction = styled.div`
  flex: none;

  @media (max-width: 600px) {
    order: 1;
    margin-left: auto;
  }
`;

const MoreRow = styled.div`
  padding: 10px 18px 12px;
  border-top: 1px solid ${(p) => p.theme.colors.grey4};
`;

const ToastBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  font-weight: 600;
`;

export type TodoListProps = {
  todos: HomeTodoDto[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
};

const rowTarget = (item: HomeTodoDto): string => {
  if (item.kind === "conversation-action") {
    return conversationUrl(item.conversationId, item.occurrenceIndex);
  }
  const action = primaryAction(item);
  return action.type === "link" ? action.to : "#";
};

const LoadingRows = () => (
  <RowList aria-hidden="true">
    {[0, 1, 2, 3, 4].map((i) => (
      <Row key={i}>
        <RowTag>
          <Skeleton width="64px" height={18} />
        </RowTag>
        <RowText>
          <Skeleton width={`${70 - i * 6}%`} mb={1} />
          <Skeleton width="40%" height={11} />
        </RowText>
        <RowAction>
          <Skeleton width="84px" height={30} />
        </RowAction>
      </Row>
    ))}
  </RowList>
);

export function TodoList({
  todos,
  isLoading,
  isError,
  onRetry,
}: TodoListProps) {
  const [savedTab, setSavedTab] = useLocalStorage<Tab>("home-todo-tab", "all");
  const [expanded, setExpanded] = useState<TodoGroupId[]>([]);
  /**
   * Optimistic completion, by to-do id: the value is the item's completedOn,
   * so null means "put it back". The server takes a moment to agree, and the
   * row keeps its place either way.
   */
  const [completions, setCompletions] = useState<Record<string, number | null>>(
    {},
  );
  const [pending, setPending] = useState<string[]>([]);
  const changeAction = useChangeActionItem();
  const invalidateHome = useInvalidateHome();
  const toast = useToast();

  const visible = useMemo(() => {
    const now = new Date();
    return (todos ?? [])
      .map((x) =>
        x.kind === "conversation-action" && x.id in completions
          ? { ...x, completedOn: completions[x.id] }
          : x,
      )
      .filter((x) => !isStaleCompletion(x, now));
  }, [todos, completions]);

  // Counted per tab twice over: what is left to do drives the badges, and
  // whether a tab has any rows at all decides if it is shown. Ticking off
  // the last item in a tab shouldn't make the tab (and the row) vanish.
  const counts = useMemo(() => {
    const empty = (): Record<Tab, number> => ({
      all: 0,
      conversations: 0,
      feedback: 0,
      goals: 0,
      learning: 0,
    });
    const open = empty();
    const rows = empty();
    for (const x of visible) {
      rows.all += 1;
      rows[x.category] += 1;
      if (!isCompleted(x)) {
        open.all += 1;
        open[x.category] += 1;
      }
    }
    return { open, rows };
  }, [visible]);

  // Hide tabs with nothing in them, but never "All".
  const tabs = tabOrder.filter((t) => t === "all" || counts.rows[t] > 0);
  const tab: Tab =
    savedTab != null && tabs.includes(savedTab) ? savedTab : "all";

  const filtered =
    tab === "all" ? visible : visible.filter((x) => x.category === tab);
  const groups = groupTodos(filtered);

  /** Ticks an action off, or puts it back. The row stays where it is. */
  const setDone = async (item: HomeTodoDto, done: boolean) => {
    if (item.kind !== "conversation-action") {
      return;
    }

    trackHome("home_todo_action", {
      kind: item.kind,
      action: done ? "complete" : "undo",
    });
    setPending((p) => p.concat(item.id));
    try {
      await changeAction.mutateAsync({
        conversationId: item.conversationId,
        actionItemId: item.actionItemId,
        value: done,
      });
      setCompletions((c) => ({
        ...c,
        [item.id]: done ? getUnixTime(new Date()) : null,
      }));
      invalidateHome();

      if (done) {
        toast({
          content: <ToastBody>Marked done</ToastBody>,
        });
      }
    } catch {
      toast({
        content: (
          <ToastBody>
            {done
              ? "Couldn't mark that done. Check your connection and try again."
              : "Couldn't undo that. Try again."}
          </ToastBody>
        ),
      });
    } finally {
      setPending((p) => p.filter((x) => x !== item.id));
    }
  };

  const renderRow = (item: HomeTodoDto) => {
    const action = primaryAction(item);
    // Once it is done, "Done 5 minutes ago" replaces the due date.
    const done = completedText(item);
    const due = done ?? dueText(item);
    const overdue = done == null && groupTodos([item]).overdue.length > 0;
    const isPending = pending.includes(item.id);

    return (
      <Row key={item.id}>
        <RowTag>
          <CategoryLabel category={item.category}>
            {tagLabel(item)}
          </CategoryLabel>
        </RowTag>
        <RowText>
          <RowTitle to={rowTarget(item)} $done={done != null}>
            {item.title}
          </RowTitle>
          {(item.context != null || due != null) && (
            <Muted m={0} mt="2px" fontSize="caption">
              {item.context}
              {item.context != null && due != null && " · "}
              {due != null && (
                <Text
                  as="b"
                  fontWeight={600}
                  color={overdue ? "danger" : "body"}
                >
                  {due}
                </Text>
              )}
            </Muted>
          )}
        </RowText>
        <RowAction>
          {action.type === "complete" ? (
            <Button
              type="button"
              small
              filled={action.done}
              color={action.done ? "positive" : undefined}
              onClick={() => setDone(item, !action.done)}
              disabled={isPending}
              aria-pressed={action.done}
              aria-label={`Mark "${item.title}" as done`}
            >
              <FaCheck aria-hidden="true" size="0.85em" />
              <span>{isPending ? "Saving…" : action.label}</span>
            </Button>
          ) : (
            <Button
              small
              filled={false}
              to={action.to}
              aria-label={`${action.label}: ${item.title}`}
              onClick={() =>
                trackHome("home_todo_action", {
                  kind: item.kind,
                  action: "open",
                })
              }
            >
              {action.label}
            </Button>
          )}
        </RowAction>
      </Row>
    );
  };

  const body = () => {
    if (isLoading) {
      return <LoadingRows />;
    }

    if (isError) {
      return (
        <LoadError
          message="We couldn't load your to-do list."
          onRetry={onRetry}
        />
      );
    }

    if (visible.length === 0) {
      return (
        <EmptyState icon={FaRegCheckCircle} title="You're all caught up">
          Nothing needs you right now. New actions and requests will show up
          here.
        </EmptyState>
      );
    }

    return groupOrder
      .filter((g) => groups[g].length > 0)
      .map((g) => {
        const items = groups[g];
        const isExpanded = expanded.includes(g);
        const shown = isExpanded ? items : items.slice(0, GROUP_PREVIEW);
        const headingId = `home-todo-${g}`;

        return (
          <div key={g} role="group" aria-labelledby={headingId}>
            <GroupHeading id={headingId} group={g}>
              {groupLabels[g]} ({items.filter((x) => !isCompleted(x)).length})
            </GroupHeading>
            <RowList>{shown.map(renderRow)}</RowList>
            {items.length > GROUP_PREVIEW && (
              <MoreRow>
                <LinkButton
                  type="button"
                  aria-expanded={isExpanded}
                  onClick={() =>
                    setExpanded((e) =>
                      isExpanded ? e.filter((x) => x !== g) : e.concat(g),
                    )
                  }
                >
                  {isExpanded
                    ? "Show fewer"
                    : `Show ${items.length - GROUP_PREVIEW} more`}
                </LinkButton>
              </MoreRow>
            )}
          </div>
        );
      });
  };

  return (
    <HomeCard title="To do" titleId="home-todo-title" headerRule={false}>
      {!isLoading && !isError && visible.length > 0 && (
        <FilterTabs
          size="small"
          ariaLabel="Filter your to-do list"
          idPrefix="home-tab"
          panelId="home-todo-panel"
          activeIndex={tabs.indexOf(tab)}
          options={tabs.map((t) => (
            <>
              {t === "all" ? "All" : categoryLabels[t]}
              <Count aria-hidden="true">{counts.open[t]}</Count>
            </>
          ))}
          onSelect={(i) => {
            setSavedTab(tabs[i]);
            trackHome("home_todo_tab", { tab: tabs[i] });
          }}
        />
      )}
      <div
        id="home-todo-panel"
        role={
          !isLoading && !isError && visible.length > 0 ? "tabpanel" : undefined
        }
        aria-labelledby={`home-tab-${tabs.indexOf(tab)}`}
        aria-busy={isLoading}
      >
        {body()}
      </div>
    </HomeCard>
  );
}
